import type { Vec3 } from '../../geo/types';

/** A builder's local frame: its origin in the world and its turn about the vertical. */
export interface Frame {
  readonly origin: Vec3;
  readonly yaw: number;
  /** Local z displacement per metre of x, for plots meeting a frontage obliquely. */
  readonly shear?: number;
}

export interface FaceMask {
  readonly top: boolean;
  readonly bottom: boolean;
  readonly sides: boolean;
}

let lastRotation: { yaw: number; cos: number; sin: number } | undefined;
function yawBasis(yaw: number) {
  // A house emits many vertices at the same turn. Check the value, including signed zero,
  // so a reused mutable frame still receives its current rotation.
  if (!lastRotation || !Object.is(lastRotation.yaw, yaw))
    lastRotation = { yaw, cos: Math.cos(yaw), sin: Math.sin(yaw) };
  return lastRotation;
}

export function transformPoint(p: Vec3, frame: Frame): Vec3 {
  const { cos, sin } = yawBasis(frame.yaw);
  const localZ = p[2] + (frame.shear ?? 0) * p[0];
  const x = p[0] * cos + localZ * sin;
  const z = -p[0] * sin + localZ * cos;
  return [x + frame.origin[0], p[1] + frame.origin[1], z + frame.origin[2]];
}

/** A vector under the frame's linear transform; retain its scale for instance matrices. */
export function transformDirection(v: Vec3, frame: Frame): Vec3 {
  const { cos, sin } = yawBasis(frame.yaw);
  const z = v[2] + (frame.shear ?? 0) * v[0];
  return [v[0] * cos + z * sin, v[1], -v[0] * sin + z * cos];
}

export function transformNormal(n: Vec3, frame: Frame): Vec3 {
  const { cos, sin } = yawBasis(frame.yaw);
  // Normals use the inverse transpose, not the point/vector transform.
  const localX = n[0] - (frame.shear ?? 0) * n[2];
  const x = localX * cos + n[2] * sin;
  const z = -localX * sin + n[2] * cos;
  const len = Math.sqrt(x * x + n[1] * n[1] + z * z);
  return [x / len, n[1] / len, z / len];
}

/** A quad's unit normal from its first corner's two edges, up for a degenerate quad. */
export function quadNormal(p0: Vec3, p1: Vec3, p3: Vec3): Vec3 {
  const x1 = p1[0] - p0[0],
    y1 = p1[1] - p0[1],
    z1 = p1[2] - p0[2];
  const x2 = p3[0] - p0[0],
    y2 = p3[1] - p0[1],
    z2 = p3[2] - p0[2];
  const nx = y1 * z2 - z1 * y2;
  const ny = z1 * x2 - x1 * z2;
  const nz = x1 * y2 - y1 * x2;
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
  return len > 0 ? [nx / len, ny / len, nz / len] : [0, 1, 0];
}

/** A planar outline's unit normal by Newell's method, up for a degenerate outline. */
export function newellNormal(outline: readonly Vec3[]): Vec3 {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < outline.length; i++) {
    const curr = outline[i]!;
    const next = outline[(i + 1) % outline.length]!;
    nx += (curr[1] - next[1]) * (curr[2] + next[2]);
    ny += (curr[2] - next[2]) * (curr[0] + next[0]);
    nz += (curr[0] - next[0]) * (curr[1] + next[1]);
  }
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
  return len > 0 ? [nx / len, ny / len, nz / len] : [0, 1, 0];
}
